import React, { useRef, useState } from "react";
import { Camera, Trash2, Save, Loader2, Mail, BadgeCheck, Phone } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import Avatar from "./Avatar";
import { readImageAsDataUrl } from "../../lib/image";
import { formatUSPhone } from "../../lib/api";

// Shared settings card with an editable profile photo + display name.
// Used by both the client portal and the back-office CRM.
// When `extended` is true (client portal), also shows a phone-type label and a full address block.
export default function ProfileSettingsCard({ user, role, onSave, accent = "navy", extended = false }) {
  const [name, setName] = useState(user?.name || "");
  const [avatar, setAvatar] = useState(user?.avatar || null);
  const [phone, setPhone] = useState(user?.phone || "");
  const [phoneLabel, setPhoneLabel] = useState(user?.phone_label || "Cell");
  const [address, setAddress] = useState(user?.address || "");
  const [address2, setAddress2] = useState(user?.address2 || "");
  const [city, setCity] = useState(user?.city || "");
  const [stateVal, setStateVal] = useState(user?.state || "");
  const [zip, setZip] = useState(user?.zip || "");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readImageAsDataUrl(file, 256);
      setAvatar(dataUrl);
    } catch (err) {
      toast.error(err.message || "Could not load image.");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name cannot be empty.");
      return;
    }
    setSaving(true);
    const payload = { name: name.trim(), avatar, phone: phone.trim() };
    if (extended) {
      Object.assign(payload, {
        phone_label: phoneLabel,
        address: address.trim(),
        address2: address2.trim(),
        city: city.trim(),
        state: stateVal.trim(),
        zip: zip.trim(),
      });
    }
    const res = await onSave(payload);
    setSaving(false);
    if (res?.ok === false) toast.error(res.error || "Could not save.");
    else toast.success("Profile updated!");
  };

  const dirty =
    name !== (user?.name || "") ||
    avatar !== (user?.avatar || null) ||
    phone !== (user?.phone || "") ||
    (extended && (
      phoneLabel !== (user?.phone_label || "Cell") ||
      address !== (user?.address || "") ||
      address2 !== (user?.address2 || "") ||
      city !== (user?.city || "") ||
      stateVal !== (user?.state || "") ||
      zip !== (user?.zip || "")
    ));

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className={`h-24 ${accent === "gold" ? "bg-gold" : "bg-navy"}`} />
      <div className="px-6 pb-6">
        <div className="-mt-12 flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="relative w-24 h-24 flex-shrink-0">
            <Avatar
              name={name}
              src={avatar}
              className="w-24 h-24 rounded-2xl bg-navy text-gold text-2xl font-serif font-bold ring-4 ring-white shadow"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full bg-gold hover:bg-gold-dark text-navy flex items-center justify-center shadow-md ring-2 ring-white transition-colors"
              aria-label="Change photo"
            >
              <Camera className="h-4 w-4" />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
            />
          </div>
          <div className="pt-3 sm:pt-16">
            <h2 className="font-serif text-2xl font-bold text-navy">{name || "Your Name"}</h2>
            <p className="text-gray-500 text-sm flex items-center gap-1.5">
              {role && <BadgeCheck className="h-4 w-4 text-gold" />}
              {role || "Member"} · Rehabilitated For Life SSM
            </p>
          </div>
          {avatar && (
            <button
              type="button"
              onClick={() => setAvatar(null)}
              className="sm:ml-auto sm:pt-16 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors"
            >
              <Trash2 className="h-4 w-4" /> Remove photo
            </button>
          )}
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Display Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="mt-1.5 max-w-md"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Contact Phone</label>
            <div className="mt-1.5 flex gap-2 max-w-md">
              <div className="relative flex-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={phone}
                  onChange={(e) => setPhone(formatUSPhone(e.target.value))}
                  placeholder="(555) 123-4567"
                  className="pl-10"
                  type="tel"
                  data-testid="profile-phone-input"
                />
              </div>
              {extended && (
                <Select value={phoneLabel} onValueChange={setPhoneLabel}>
                  <SelectTrigger className="w-28" data-testid="profile-phone-label">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cell">Cell</SelectItem>
                    <SelectItem value="Home">Home</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          {extended && (
            <div data-testid="profile-address-block">
              <label className="text-sm font-medium text-gray-700">Address</label>
              <div className="mt-1.5 space-y-2 max-w-md">
                <Input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Street address"
                  data-testid="profile-address-street"
                />
                <Input
                  value={address2}
                  onChange={(e) => setAddress2(e.target.value)}
                  placeholder="Apt, suite, unit (optional)"
                  data-testid="profile-address-street2"
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    className="col-span-2 sm:col-span-1"
                    data-testid="profile-address-city"
                  />
                  <Input
                    value={stateVal}
                    onChange={(e) => setStateVal(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="State"
                    maxLength={2}
                    data-testid="profile-address-state"
                  />
                  <Input
                    value={zip}
                    onChange={(e) => setZip(e.target.value.replace(/[^\d-]/g, "").slice(0, 10))}
                    placeholder="ZIP"
                    data-testid="profile-address-zip"
                  />
                </div>
              </div>
            </div>
          )}
          {user?.email && (
            <div>
              <label className="text-sm font-medium text-gray-700">Email</label>
              <div className="mt-1.5 flex items-center gap-2 max-w-md bg-cream rounded-md px-3 py-2.5 text-sm text-gray-600">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="break-all">{user.email}</span>
              </div>
            </div>
          )}
          <p className="text-xs text-gray-400">
            Tip: A square photo works best. Images are resized automatically.
          </p>
          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-navy hover:bg-navy-light text-white font-semibold"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-1.5" /> Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
